// Reglas del Cazador de Ofertas por cuenta — vista GLOBAL de solo lectura.
// EXCLUSIVO del administrador del sistema (system_admin).
//
// Muestra, para cada tenant, las reglas y la frecuencia con que corre su
// cazador de ofertas (estado, cadencia, descuento, condiciones, precio tope,
// email de aviso y última corrida). No permite editar: la edición es por cuenta
// (owner) en /config/deal-hunter. Consume `GET /admin/deal-hunter-configs`.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { AsyncBoundary, Badge, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type { TenantDealHunterConfig } from '../types.js';

function fmtDate(t: TFunction, iso: string | null): string {
  if (!iso) return t('globalDealHunter.never');
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function freqLabel(t: TFunction, hours: number): string {
  if (hours === 24) return t('globalDealHunter.freqDaily');
  if (hours === 168) return t('globalDealHunter.freqWeekly');
  if (hours < 24) return t('globalDealHunter.freqHours', { hours });
  const days = Math.round(hours / 24);
  return t('globalDealHunter.freqDays', { days });
}

export function GlobalDealHunterView() {
  const { t } = useTranslation();
  const api = useApi();
  const configs = useAsync(() => api.admin.dealHunterConfigs(), []);
  const [search, setSearch] = useState('');

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('globalDealHunter.pageTitle')}
        subtitle={t('globalDealHunter.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <div className="field-slot">
          <Icon name="search" className="text-on-surface-variant text-[18px] mr-space-xs" />
          <input
            className="field-input"
            placeholder={t('globalDealHunter.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <AsyncBoundary state={configs}>
          {(data) => <ConfigList items={data.items} search={search} />}
        </AsyncBoundary>
      </div>
    </div>
  );
}

function ConfigList({ items, search }: { items: TenantDealHunterConfig[]; search: string }) {
  const { t } = useTranslation();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return items;
    return items.filter(
      (c) =>
        (c.tenant_name ?? '').toLowerCase().includes(q) || (c.owner_email ?? '').toLowerCase().includes(q),
    );
  }, [items, search]);

  if (filtered.length === 0) {
    return <p className="font-body-sm text-body-sm text-on-surface-variant">{t('globalDealHunter.noMatches')}</p>;
  }

  const activeCount = filtered.filter((c) => c.enabled).length;

  return (
    <div className="flex flex-col gap-space-sm">
      <span className="font-code-catalog text-body-sm text-on-surface-variant">
        {t('globalDealHunter.summary', { count: filtered.length, active: activeCount })}
      </span>
      {filtered.map((c) => (
        <div key={c.tenant_id} className="surface-card flex flex-col gap-space-xs">
          <div className="flex items-center justify-between gap-space-sm">
            <span className="flex items-center gap-space-xs min-w-0">
              <Icon name="travel_explore" className="text-primary text-[18px]" />
              <span className="flex flex-col min-w-0">
                <span className="font-headline-sm text-headline-sm text-on-surface truncate">
                  {c.tenant_name ?? c.tenant_id}
                </span>
                {c.owner_email && (
                  <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
                    {c.owner_email}
                  </span>
                )}
              </span>
            </span>
            <Badge tone={c.enabled ? 'success' : 'neutral'}>{c.enabled ? t('globalDealHunter.active') : t('globalDealHunter.paused')}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-space-xs font-body-sm text-body-sm">
            <Fact icon="schedule" label={t('globalDealHunter.frequency')} value={freqLabel(t, c.frequency_hours)} />
            <Fact icon="percent" label={t('globalDealHunter.minDiscount')} value={`${c.discount_threshold_pct}%`} />
            <Fact icon="grade" label={t('globalDealHunter.topConditions')} value={String(c.top_conditions)} />
            <Fact icon="sell" label={t('globalDealHunter.maxPrice')} value={c.max_price === null ? t('globalDealHunter.noCap') : String(c.max_price)} />
            <Fact icon="mail" label={t('globalDealHunter.notify')} value={c.notify_email ?? '—'} />
            <Fact icon="history" label={t('globalDealHunter.lastRun')} value={fmtDate(t, c.last_run_at)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Fact({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <span className="flex items-center gap-space-xs min-w-0">
      <Icon name={icon} className="text-on-surface-variant text-[16px] shrink-0" />
      <span className="text-on-surface-variant">{label}:</span>
      <span className="text-on-surface truncate" title={value}>
        {value}
      </span>
    </span>
  );
}
